import { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import {
  AddedPage,
  DeletedPage,
  UpdatedPage,
} from '../../../../../../../applications/ports/versionReportVersionRepository'
import { useVersionReportVersionRepository } from '../../../../../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import { VersionReportVersionEntity } from '../../../../../../../domain/entity/version-report/VersionReportVersionEntity'
import {
  addGlobalMessage,
  MessageLevel,
} from '../../../../../../../store/messages'
import { intl } from '../../../../../../../i18n'
import { getErrorCode } from '../../../../../../../infrastructure/api/error'
import { buildConfigJsonString } from '../../../../hooks/versionReportConfigJson'
import {
  buildInitialRows,
  buildRowDiff,
} from '../../../../hooks/versionReportRowDiff'
import { VersionReportVersionPageFormModel } from '../model/VersionReportVersionPageFormModel'

export type UseVersionConfigDialogProps = {
  original: VersionReportVersionEntity
  onAfterSubmit: () => void | Promise<void>
  onClose: () => void
  resetPageEntityMap: () => void
}

export type UseVersionConfigDialogReturn = {
  loading: boolean
  handleSubmit: (pages: VersionReportVersionPageFormModel[]) => Promise<void>
  handleClose: () => void
}

const buildStructureChanges = (
  original: VersionReportVersionEntity,
  pages: VersionReportVersionPageFormModel[]
): {
  added: AddedPage[]
  updated: UpdatedPage[]
  deletedPages: DeletedPage[]
} => {
  const originalByUuid = new Map(original.pages.map(p => [p.uuid, p] as const))
  const currentUuids = new Set(
    pages.map(p => p.uuid).filter((uuid): uuid is string => uuid !== null)
  )

  const added: AddedPage[] = pages
    .filter(p => p.uuid === null)
    .map(p => ({
      pageType: p.pageType,
      pageNumber: p.pageNumber,
      pageTitle: p.pageTitle,
      configJsonString: buildConfigJsonString(p.config),
      initialRows: buildInitialRows(p.config),
    }))

  const updated: UpdatedPage[] = pages
    .filter(
      (p): p is VersionReportVersionPageFormModel & { uuid: string } =>
        p.uuid !== null
    )
    .flatMap(p => {
      const orig = originalByUuid.get(p.uuid)
      if (!orig) return []
      const { addedRows, deletedRows } = buildRowDiff(p.config, orig.rows)
      const configJsonString = buildConfigJsonString(p.config)
      const changed =
        orig.pageNumber !== p.pageNumber ||
        orig.pageTitle !== p.pageTitle ||
        buildConfigJsonString(orig.config) !== configJsonString ||
        addedRows.length > 0 ||
        deletedRows.length > 0
      if (!changed) return []
      return [
        {
          pageUuid: p.uuid,
          lockVersion: orig.lockVersion,
          pageNumber: p.pageNumber,
          pageTitle: p.pageTitle,
          configJsonString,
          addedRows,
          deletedRows,
        },
      ]
    })

  const deletedPages: DeletedPage[] = original.pages
    .filter(op => !currentUuids.has(op.uuid))
    .map(op => ({ uuid: op.uuid, lockVersion: op.lockVersion }))

  return { added, updated, deletedPages }
}

export const useVersionConfigDialog = ({
  original,
  onAfterSubmit,
  onClose,
  resetPageEntityMap,
}: UseVersionConfigDialogProps): UseVersionConfigDialogReturn => {
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const { applyStructureChanges } = useVersionReportVersionRepository()

  const handleSubmit = useCallback(
    async (pages: VersionReportVersionPageFormModel[]) => {
      try {
        setLoading(true)
        const { added, updated, deletedPages } = buildStructureChanges(
          original,
          pages
        )
        await applyStructureChanges({
          versionUuid: original.uuid,
          added,
          updated,
          deletedPages,
        })
        await onAfterSubmit()
        resetPageEntityMap()
      } catch (error) {
        if (getErrorCode(error) === 'CONSISTENCY_VIOLATION') {
          await onAfterSubmit()
          resetPageEntityMap()
          dispatch(
            addGlobalMessage({
              type: MessageLevel.WARN,
              title: intl.formatMessage({ id: 'versionReport.conflict.title' }),
              text: intl.formatMessage({
                id: 'versionReport.conflict.message',
              }),
            })
          )
          return
        }
        dispatch(
          addGlobalMessage({
            type: MessageLevel.ERROR,
            title: '構成更新エラー',
            text: '構成の更新に失敗しました',
          })
        )
        throw error
      } finally {
        setLoading(false)
      }
    },
    [
      original,
      applyStructureChanges,
      onAfterSubmit,
      resetPageEntityMap,
      dispatch,
    ]
  )

  const handleClose = useCallback(() => {
    if (!loading) {
      onClose()
    }
  }, [loading, onClose])

  return {
    loading,
    handleSubmit,
    handleClose,
  }
}
